// Creep types — the vocabulary every creep field shares (inspections, ADs / SBs,
// modifications, model templates). Mirrors the backend's
// app/inspections/completion.py CREEP_TYPE_LABELS and creep.creep_label.
//
// 6 (Progressive) is the progressive machinery's, not a user choice: it is left out
// of every select (AN-6805; legacy's own select omits it,
// aircraft_templates_tab_general_inspection.inc:294-299), but a stored 6 is still
// read and shown (Aaron 2026-10-07, ruling 5).
export const CREEP_TYPE_LABELS: Record<number, string> = {
  0: "No Creep",
  1: "Creep on Extension",
  2: "Creep on Tolerance",
  3: "Creep on Completion Unit",
  4: "End of Month",
  5: "Creep only when finished early",
};

export const PROGRESSIVE_CREEP_TYPE = 6;
export const END_OF_MONTH_CREEP_TYPE = 4;
export const COMPLETION_UNIT_CREEP_TYPE = 3;

/** The backend's is_date_axis: "date" anywhere in Based On, any case. */
export function isDateAxis(basedOn: string | null | undefined): boolean {
  return String(basedOn ?? "").toLowerCase().includes("date");
}

/** What a stored creep type reads as. null/undefined → "" (nothing recorded). */
export function creepTypeLabel(value: number | null | undefined): string {
  if (value === null || value === undefined || (value as unknown) === "") return "";
  const n = Number(value);
  if (n === PROGRESSIVE_CREEP_TYPE) return "Progressive";
  return CREEP_TYPE_LABELS[n] ?? `${n} (not a legacy creep type)`;
}

/**
 * The options a limitation's creep select offers. End of Month only on a date
 * axis: off one, legacy degrades it to Creep on Completion Unit at completion
 * (:1610-1612), so offering it would promise a month-end snap that never happens.
 * A stored value that is not one of them (6, or 4 on an hours line) is kept as its
 * own option, so opening a form never silently re-sets it.
 */
export function limitationCreepOptions(
  basedOn: string | null | undefined,
  current?: number | null
): Array<{ value: number; label: string }> {
  const options = Object.entries(CREEP_TYPE_LABELS)
    .map(([value, label]) => ({ value: Number(value), label }))
    .filter((o) => o.value !== END_OF_MONTH_CREEP_TYPE || !basedOn || isDateAxis(basedOn));
  if (current !== null && current !== undefined && !options.some((o) => o.value === Number(current))) {
    options.push({ value: Number(current), label: creepTypeLabel(current) });
  }
  return options;
}

/** The master's creep type as it applies to ONE line: End of Month degrades to
 *  Creep on Completion Unit off a date axis (completion.resolve_next_due_base). */
export function creepAsApplied(creepType: number | null | undefined, basedOn: string | null | undefined) {
  if (creepType === null || creepType === undefined) return creepType;
  return Number(creepType) === END_OF_MONTH_CREEP_TYPE && basedOn && !isDateAxis(basedOn)
    ? COMPLETION_UNIT_CREEP_TYPE
    : Number(creepType);
}

/**
 * A limitation line's creep, as the backend serves it on every reader
 * (app/inspections/creep.py creep_fields → `creep`): the line's OWN values
 * (null = inherits), the EFFECTIVE ones a completion uses, and where each came from.
 */
export interface LimitationCreep {
  creep_type: number | null;
  tolerance: number | null;
  creep_type_effective: number | null;
  creep_type_effective_label: string | null;
  creep_type_source: "set" | "inherited" | null;
  tolerance_effective: number | null;
  tolerance_source: "set" | "inherited" | null;
  master_creep_type: number | null;
  master_tolerance: number | null;
}

/** The line's own creep type / tolerance as a request body: blank → null = inherit. */
export interface OwnCreepValues {
  creep_type: number | null;
  tolerance: number | null;
}

export const INHERIT_CREEP: OwnCreepValues = { creep_type: null, tolerance: null };

export function ownCreepFrom(creep: LimitationCreep | null | undefined): OwnCreepValues {
  return { creep_type: creep?.creep_type ?? null, tolerance: creep?.tolerance ?? null };
}
